import {
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { FilterList, Close } from "@mui/icons-material";
import FileSearch from "components/fileStorage/FileSearch";
import PaginationControls from "components/fileStorage/PaginationControls";
import DateFilter from "components/integrations-list/DateFilter";
import {
  GetAllVendorsParams,
  lastRunFilterOptions,
  nextRunFilterOptions,
  useGetAllVendorsQuery,
} from "features/integrations/integrations";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DEFAULT_VALUES, userPreferencesStorage } from "utils/localStorage";
import IntegrationsTable from "../components/integrations-list/IntegrationsTable";
import Layout from "../components/Layout";

export default function IntegrationsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
  const defaultNext = nextRunFilterOptions[0].value;
  const defaultLast = lastRunFilterOptions[0].value;
  const [searchParams, setSearchParams] = useSearchParams();

  const savedPrefs = userPreferencesStorage.getUserPreferences();
  const savedFilters =
    savedPrefs.integrationFilters || DEFAULT_VALUES.INTEGRATION_FILTERS;

  const [nextRunFilter, setNextRunFilter] = useState<string>(
    savedFilters.nextRunFilter || defaultNext,
  );
  const [lastRunFilter, setLastRunFilter] = useState<string>(
    savedFilters.lastRunFilter || defaultLast,
  );

  const [search, setSearch] = useState<string>(
    (searchParams.get("search") as string | null) ?? savedFilters.search ?? "",
  );

  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(
    savedPrefs.integrationPaginationValue ||
      DEFAULT_VALUES.INTEGRATION_PAGINATION_VALUE,
  );
  const [sortBy, setSortBy] = useState<GetAllVendorsParams["sort_by"]>();
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [useIntegrationTimezone, setUseIntegrationTimezone] = useState(true);

  const buildFiltersArgs = () => {
    const args: Record<string, string | number | undefined> = {
      page,
      page_size: pageSize,
    };

    if (sortBy) args.sort_by = sortBy;
    args.sort_order = sortOrder;

    if (nextRunFilter !== defaultNext) {
      args.next_run = nextRunFilter;
    }

    if (lastRunFilter !== defaultLast) {
      args.last_run = lastRunFilter;
    }

    if (search.trim()) {
      args.search = search.trim();
    }

    return args;
  };

  const filtersArgs = useMemo(
    () => buildFiltersArgs(),
    [
      lastRunFilter,
      nextRunFilter,
      defaultLast,
      defaultNext,
      search,
      page,
      pageSize,
      sortBy,
      sortOrder,
    ],
  );

  const { data, isLoading, isFetching, error } = useGetAllVendorsQuery(
    filtersArgs,
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const integrations = data?.results ?? [];
  const totalItems = data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / pageSize);

  const handleLastRunChange = (newInternalValue: string) => {
    setLastRunFilter(newInternalValue);
    setNextRunFilter(defaultNext);
    setPage(1);
    userPreferencesStorage.setUserPreferences({
      integrationFilters: {
        nextRunFilter: defaultNext,
        lastRunFilter: newInternalValue,
        search,
      },
    });
  };

  const handleNextRunChange = (newInternalValue: string) => {
    setNextRunFilter(newInternalValue);
    setLastRunFilter(defaultLast);
    setPage(1);
    userPreferencesStorage.setUserPreferences({
      integrationFilters: {
        nextRunFilter: newInternalValue,
        lastRunFilter: defaultLast,
        search,
      },
    });
  };

  const handleSortChange = (field: GetAllVendorsParams["sort_by"]) => {
    if (sortBy === field) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else if (sortOrder === "desc") {
        setSortBy(undefined as any);
        setSortOrder("asc");
      }
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setPage(1);
  };

  const clearFilters = () => {
    setSearch("");
    setLastRunFilter(defaultLast);
    setNextRunFilter(defaultNext);
    setPage(1);
    userPreferencesStorage.setUserPreferences({
      integrationFilters: {
        nextRunFilter: defaultNext,
        lastRunFilter: defaultLast,
        search: "",
      },
    });
    setSearchParams((params) => {
      params.delete("search");
      return params;
    });
    if (isMobile) {
      setFiltersDrawerOpen(false);
    }
  };

  const renderFilters = () => (
    <>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Typography
          sx={{
            fontWeight: "bold",
            color: "primary.main",
            whiteSpace: "nowrap",
          }}
        >
          Last Run:
        </Typography>
        <DateFilter
          options={lastRunFilterOptions}
          selectedValue={lastRunFilter}
          onValueChange={handleLastRunChange}
        />
      </Box>

      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Typography
          sx={{
            fontWeight: "bold",
            color: "primary.main",
            whiteSpace: "nowrap",
          }}
        >
          Next Run:
        </Typography>
        <DateFilter
          options={nextRunFilterOptions}
          selectedValue={nextRunFilter}
          onValueChange={handleNextRunChange}
        />
      </Box>

      <Box mb={2}>
        <FileSearch
          onSearch={(term) => {
            setSearch(term || "");
            setPage(1);
            setSearchParams((params) => {
              if (term) {
                params.set("search", term);
              } else {
                params.delete("search");
              }
              return params;
            });
            userPreferencesStorage.setUserPreferences({
              integrationFilters: {
                nextRunFilter,
                lastRunFilter,
                search: term || "",
              },
            });
          }}
          placeholder="Search..."
          initialValue={search}
        />
      </Box>

      <Box mb={2}>
        <Button variant="contained" color="primary" onClick={clearFilters} fullWidth={isMobile}>
          Clear Filters
        </Button>
      </Box>

      <Box display="flex" alignItems="center" gap={1} height={40}>
        <Typography
          sx={{
            fontWeight: "bold",
            color: "primary.main",
            whiteSpace: "nowrap",
          }}
        >
          Time:
        </Typography>
        <ToggleButtonGroup
          value={useIntegrationTimezone ? "integration" : "local"}
          exclusive
          onChange={(_, value: string | null) => {
            if (value !== null) {
              setUseIntegrationTimezone(value === "integration");
            }
          }}
          size="small"
          sx={{ height: "100%", "& .MuiToggleButton-root": { py: 0 } }}
        >
          <ToggleButton value="integration" aria-label="Integration timezone">
            Integration
          </ToggleButton>
          <ToggleButton value="local" aria-label="Local time">
            Local
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </>
  );

  return (
    <Layout title="Integrations" content="Integrations">
      <Container
        component="main"
        maxWidth={false}
        sx={{
          py: 3,
          px: { xs: 1.5, sm: 3 },
          height: "calc(100vh - 200px)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {isMobile ? (
          <Box mb={2} display="flex" alignItems="center" gap={1}>
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => setFiltersDrawerOpen(true)}
              sx={{ flex: 1 }}
            >
              Filters
            </Button>
          </Box>
        ) : (
          <Box
            mb={2}
            display="flex"
            flexDirection="row"
            alignItems="center"
            flexWrap="wrap"
            gap={3}
          >
            {renderFilters()}
          </Box>
        )}
        <Box
          sx={{
            backgroundColor: "background.paper",
            borderRadius: 2,
            boxShadow: 1,
            border: "1px solid",
            borderColor: "divider",
            overflow: "clip",
            flex: 1,
            minHeight: 0,
          }}
        >
          <IntegrationsTable
            integrations={integrations}
            isLoading={isLoading}
            isFetching={isFetching}
            error={error}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
            useIntegrationTimezone={useIntegrationTimezone}
            pagination={
              totalItems > 0 ? (
                <Box
                  sx={{
                    position: "relative",
                    borderTop: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <PaginationControls
                    currentLimit={pageSize}
                    onLimitChange={(limit) => {
                      setPageSize(limit);
                      setPage(1);
                      userPreferencesStorage.setUserPreferences({
                        integrationPaginationValue: limit,
                      });
                    }}
                    totalItems={totalItems}
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    showLimitSelector={true}
                    showPageInfo={true}
                    disabled={isFetching}
                    size={"small"}
                    pageTokens={[]}
                    hasNextPage={page < totalPages}
                    borderStyle={{ borderTop: "none", borderColor: "none" }}
                    buttonStyle={{
                      border: "1px solid #e5e7eb",
                      borderColor: "",
                    }}
                  />
                </Box>
              ) : null
            }
          />
        </Box>
      </Container>

      
      {isMobile && (
        <Drawer
          anchor="bottom"
          open={filtersDrawerOpen}
          onClose={() => setFiltersDrawerOpen(false)}
          PaperProps={{
            sx: {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              maxHeight: "85vh",
            },
          }}
        >
          <Box sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h6" fontWeight="bold" color="primary.main">
                Filters
              </Typography>
              <IconButton onClick={() => setFiltersDrawerOpen(false)} size="small">
                <Close />
              </IconButton>
            </Box>
            {renderFilters()}
          </Box>
        </Drawer>
      )}
    </Layout>
  );
}
